import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { LogOut, Calendar, Crown, Play, Lock, CheckCircle2 } from "lucide-react";
import wtaLogo from "@/assets/wta-logo.png";
import { isReleased, formatReleaseDate, releaseDateForOrderIndex } from "@/lib/lessonSchedule";

type LessonListRow = {
  slug: string;
  title: string;
  subtitle: string | null;
  order_index: number;
  video_url: string | null;
  homework_questions: unknown;
};

const Members = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [lessons, setLessons] = useState<LessonListRow[]>([]);
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const { openCheckout, checkoutElement } = useStripeCheckout();

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login", { replace: true });
        return;
      }
      setUser(session.user);
      const { data: enrollment } = await supabase
        .from("enrollments")
        .select("id")
        .eq("product_id", "wta_academy_enrollment")
        .in("status", ["paid", "scholarship"])
        .or(`user_id.eq.${session.user.id},email.eq.${session.user.email}`)
        .limit(1)
        .maybeSingle();
      const access = !!enrollment;
      setHasAccess(access);

      if (access) {
        const [{ data: lessonRows }, { data: subs }] = await Promise.all([
          supabase
            .from("lessons")
            .select("slug, title, subtitle, order_index, video_url, homework_questions")
            .eq("is_published", true)
            .order("order_index", { ascending: true }),
          supabase
            .from("homework_submissions")
            .select("lesson_slug")
            .eq("user_id", session.user.id),
        ]);
        setLessons((lessonRows || []) as LessonListRow[]);
        setCompleted(new Set((subs || []).map((s) => s.lesson_slug)));
      }
      setLoading(false);
    };
    init();
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Unlocked = time-released (weekly on Mondays at noon ET) AND all prior homework done.
  const unlockedSet = new Set<string>();
  {
    let priorHwOk = true;
    for (const l of lessons) {
      if (priorHwOk && isReleased(l.order_index)) unlockedSet.add(l.slug);
      const hasHw = Array.isArray(l.homework_questions) && (l.homework_questions as unknown[]).length > 0;
      if (hasHw && !completed.has(l.slug)) priorHwOk = false;
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-gold/10 bg-background/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="container mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={wtaLogo} alt="WTA" className="w-8 h-8 object-contain" />
            <span className="font-display text-lg font-semibold text-gradient-gold">Members</span>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-muted-foreground hover:text-gold font-body text-sm">
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </header>

      <main className="container mx-auto px-6 md:px-12 py-12 md:py-16 space-y-12">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.4em] text-gold font-body">Welcome back</p>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground">
            {user?.email}
          </h1>
        </div>

        {!hasAccess ? (
          <div className="border border-gold/20 bg-card/60 p-8 space-y-4">
            <h2 className="font-display text-2xl font-bold text-foreground">No academy enrollment found</h2>
            <p className="text-muted-foreground font-body">
              We couldn't find an academy enrollment linked to <span className="text-foreground">{user?.email}</span>.
              If you paid with a different email, sign out and use that email to sign in.
            </p>
            <a href="/#pricing" className="inline-block px-6 py-3 bg-gradient-gold text-primary-foreground font-body text-xs font-semibold uppercase tracking-[0.2em]">
              View pricing
            </a>
          </div>
        ) : (
          <>
            <section className="space-y-6">
              <div className="flex items-baseline justify-between gap-4">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground">Curriculum</h2>
                <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-body">
                  Complete each in order
                </p>
              </div>
              <div className="grid gap-4">
                {lessons.map((l) => {
                  const unlocked = unlockedSet.has(l.slug);
                  const done = completed.has(l.slug);
                  const label = l.order_index === 0 ? "Welcome" : `Lesson ${l.order_index}`;
                  const inner = (
                    <div
                      className={`flex items-center gap-4 md:gap-6 border p-5 md:p-6 transition ${
                        unlocked
                          ? "border-gold/25 bg-card/60 hover:border-gold/60 hover:bg-card/80 cursor-pointer"
                          : "border-gold/10 bg-card/30 opacity-70 cursor-not-allowed"
                      }`}
                    >
                      <div className="w-12 h-12 flex items-center justify-center border border-gold/30 shrink-0">
                        {!unlocked ? (
                          <Lock className="w-5 h-5 text-muted-foreground" />
                        ) : done ? (
                          <CheckCircle2 className="w-5 h-5 text-gold" />
                        ) : (
                          <Play className="w-5 h-5 text-gold" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.35em] text-gold font-body">{label}</p>
                        <h3 className="font-display text-lg md:text-xl font-semibold text-foreground truncate">
                          {l.title}
                        </h3>
                        {l.subtitle && (
                          <p className="text-muted-foreground font-body text-sm truncate">{l.subtitle}</p>
                        )}
                        {!unlocked && !isReleased(l.order_index) && (
                          <p className="text-muted-foreground font-body text-xs mt-1">
                            Unlocks {formatReleaseDate(l.order_index)}
                          </p>
                        )}
                      </div>
                      <div className="hidden md:block text-xs uppercase tracking-[0.25em] font-body">
                        {!unlocked ? (
                          <span className="text-muted-foreground">
                            {!isReleased(l.order_index) ? "Releases Mon" : "Locked"}
                          </span>
                        ) : done ? (
                          <span className="text-gold">Complete</span>
                        ) : (
                          <span className="text-foreground">Start →</span>
                        )}
                      </div>
                    </div>
                  );
                  return unlocked ? (
                    <Link key={l.slug} to={`/members/${l.slug}`}>{inner}</Link>
                  ) : (
                    <div key={l.slug}>{inner}</div>
                  );
                })}
              </div>
            </section>

            <section className="grid md:grid-cols-1 gap-6">
              <div className="border border-gold/20 bg-card/60 p-8 space-y-4">
                <Crown className="w-8 h-8 text-gold" />
                <h2 className="font-display text-xl font-bold text-foreground">Student Rate Session</h2>
                <p className="text-muted-foreground font-body text-sm">
                  Book a private 60-minute session with Angela at the enrollee-only rate of <span className="text-gold font-semibold">$65</span> (regular $125).
                </p>
                <button
                  onClick={() =>
                    openCheckout({
                      priceId: "wta_session_student",
                      customerEmail: user?.email,
                      userId: user?.id,
                    })
                  }
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-gold text-primary-foreground font-body text-xs font-semibold uppercase tracking-[0.2em]"
                >
                  <Calendar size={16} /> Book at $65
                </button>
              </div>
            </section>
          </>
        )}
      </main>
      {checkoutElement}
    </div>
  );
};

export default Members;
